import { useQuery } from '@tanstack/react-query';
import { formatDate } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { Avatar } from '@/components/avatar';

const WITHIN_DAYS = 30;

/** Who to celebrate this month and next — the ChMS gap this widget closes:
 *  `birthDate` has been captured on every person since day one, but nothing
 *  ever surfaced it back to the people who'd actually act on it. */
export function BirthdaysWidget() {
  const { t, locale } = useI18n();
  const { can } = useAuth();

  const birthdays = useQuery({
    queryKey: ['dashboard', 'birthdays'],
    queryFn: ({ signal }) =>
      peopleApi.listUpcomingBirthdays({ withinDays: WITHIN_DAYS, limit: 8 }, signal),
    enabled: can('people.person.read'),
  });

  if (!can('people.person.read')) return null;

  return (
    <div className="dashboard-category">
      <p className="dashboard-category-label">{t('dashboard.birthdays.title')}</p>

      {!birthdays.data ? null : birthdays.data.length === 0 ? (
        <p className="dashboard-birthdays-empty">
          {t('dashboard.birthdays.empty', { values: { days: WITHIN_DAYS } })}
        </p>
      ) : (
        <ul className="dashboard-birthdays-list">
          {birthdays.data.map((person) => (
            <li className="dashboard-birthday-row" key={person.id}>
              <Avatar photoFileId={person.photoFileId} displayName={person.displayName} size={36} />
              <div className="dashboard-birthday-info">
                <span className="dashboard-birthday-name">{person.displayName}</span>
                <span className="dashboard-birthday-age">
                  {t('dashboard.birthdays.turningAge', { values: { age: person.turningAge } })}
                </span>
              </div>
              <span className="dashboard-birthday-when" dir="ltr">
                {person.daysUntil === 0
                  ? t('dashboard.birthdays.today')
                  : person.daysUntil === 1
                    ? t('dashboard.birthdays.tomorrow')
                    : formatDate(person.nextBirthdayDate, locale, {
                        month: 'short',
                        day: 'numeric',
                      })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
